import { useStore } from '@nanostores/react';
import {
  useCallback,
  useEffect,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type UIEvent,
} from 'react';
import { toast } from 'react-toastify';
import { requestWithAuth } from '~/lib/.client/request-with-auth';
import { CLIENT_ORIGIN } from '~/lib/constants';
import { updateChat } from '~/lib/persistence/chats';
import { descriptionStore, setDescription } from '~/lib/stores/description';
import { projectStore } from '~/lib/stores/project';

const requestTitleChangeWithAuth = requestWithAuth(CLIENT_ORIGIN);

const titleLengthConfig = {
  min: 1,
  max: 100,
  minMessage: 'Project title is required',
  maxMessage: 'Title is too long (100 characters max)',
};

function getLengthError(value: string) {
  if (value.length < titleLengthConfig.min) {
    return titleLengthConfig.minMessage;
  } else if (value.length > titleLengthConfig.max) {
    return titleLengthConfig.maxMessage;
  }

  return undefined;
}

interface UseProjectRenameOptions {
  onTitleChange?: (title: string) => void;
}

export function useProjectRename({ onTitleChange }: UseProjectRenameOptions = {}) {
  const currentTitle = useStore(descriptionStore);
  const [localTitle, setLocalTitle] = useState(currentTitle || '');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // sync local title with global store when it changes externally
  useEffect(() => {
    setLocalTitle(currentTitle || '');
  }, [currentTitle]);

  const validateTitle = useCallback((title: string) => {
    const lengthError = getLengthError(title);

    if (lengthError) {
      setErrorMessage(lengthError);

      return false;
    }

    setErrorMessage(null);

    return true;
  }, []);

  const updateTitle = useCallback(
    async (event?: UIEvent | FormEvent) => {
      if (event) {
        event.preventDefault();
      }

      // store original title for optimistic update
      const originalTitle = currentTitle || '';
      const trimmedTitle = localTitle.trim();

      if (!validateTitle(trimmedTitle)) {
        return;
      }

      const slug = projectStore.getSlug();
      const projectId = projectStore.getProjectId();

      if (!slug || !projectId) {
        toast.error('Failed to update project title');

        return;
      }

      try {
        // optimistically update the global store
        setDescription(trimmedTitle);
        onTitleChange?.(trimmedTitle);

        await requestTitleChangeWithAuth(
          `/api/projects/${slug}`,
          {
            project: { title: trimmedTitle },
          },
          'PATCH',
        );

        updateChat(projectId, { description: trimmedTitle });

        toast.success('Project title updated');
      } catch {
        // revert to original title if the request fails
        setDescription(originalTitle);
        setLocalTitle(originalTitle);
        onTitleChange?.(originalTitle);

        toast.error('Failed to update project title');
      }
    },
    [currentTitle, localTitle, validateTitle, onTitleChange],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (errorMessage) {
        setErrorMessage(null);
      }

      setLocalTitle(event.target.value);
    },
    [errorMessage],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !event.shiftKey && !event.altKey) {
        updateTitle(event);
      }
    },
    [updateTitle],
  );

  return {
    newTitle: localTitle,
    setNewTitle: setLocalTitle,
    errorMessage,
    updateTitle,
    handleChange,
    handleKeyDown,
    validateTitle,
    isChanged: localTitle !== (currentTitle || ''),
  };
}
